import { trackCustomWarning } from './datadog-rum';

/**
 * OneTrust group to Shopify Customer Privacy field name.
 *
 * C0001 (Strictly Necessary): always allowed, no field mapping.
 * C0005 (Social Media): no Shopify mapping, unrelated to CPRA scope.
 *
 * `sale_of_data` is derived from C0004 in `buildConsentPayload` rather than
 * mapped here. See spec section "Category mapping".
 */
export const ONETRUST_TO_SHOPIFY = {
  C0002: 'analytics',
  C0003: 'preferences',
  C0004: 'marketing',
} as const;

export type ShopifyConsentPayload = {
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  sale_of_data: boolean;
};

/**
 * Build a Shopify Customer Privacy payload from OneTrust active groups.
 *
 * Rules:
 *  - All four fields default to `false` (fail-closed).
 *  - C0002 grants analytics.
 *  - C0003 grants preferences.
 *  - C0004 grants BOTH marketing AND sale_of_data (CPRA sharing override).
 *
 * `sale_of_data` is the Shopify API field name. Seed does not sell personal
 * information; we send `sale_of_data: false` to honor the CPRA right to opt out
 * of sharing for cross-context behavioral advertising.
 */
export function buildConsentPayload(
  activeGroups: string,
): ShopifyConsentPayload {
  const groups = (activeGroups || '')
    .split(',')
    .map((g) => g.trim())
    .filter(Boolean);

  const hasC0004 = groups.includes('C0004');

  return {
    analytics: groups.includes('C0002'),
    marketing: hasC0004,
    preferences: groups.includes('C0003'),
    sale_of_data: hasC0004,
  };
}

const launchCookieConsent = () => {
  window.OneTrust?.ToggleInfoDisplay?.();
};

export { launchCookieConsent };

/**
 * Return OneTrust active groups as a cleaned comma-separated string
 * (no leading/trailing commas, no whitespace). E.g. "C0001,C0002,C0004".
 * Returns empty string when groups are unavailable.
 */
export function getCleanedActiveGroups(): string {
  if (typeof window === 'undefined') return '';
  return (window.OnetrustActiveGroups || '')
    .split(',')
    .map((g) => g.trim())
    .filter(Boolean)
    .join(',');
}

const SHOPIFY_API_POLL_INTERVAL_MS = 500;
const SHOPIFY_API_POLL_TIMEOUT_MS = 10_000;

/**
 * Polls `window.Shopify.customerPrivacy` until it appears or the timeout
 * elapses. Same shape as the polling pattern in lib/datadog-rum.ts:120-138,
 * but with a longer cadence (500ms vs 100ms) and ceiling (10s vs 5s) since
 * the Shopify script is external, not bundled, and detection latency below
 * ~500ms isn't load-bearing for consent transit.
 */
export function waitForShopifyCustomerPrivacy(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.Shopify?.customerPrivacy?.setTrackingConsent) {
    return Promise.resolve(true);
  }

  return new Promise<boolean>((resolve) => {
    const interval = setInterval(() => {
      if (window.Shopify?.customerPrivacy?.setTrackingConsent) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve(true);
      }
    }, SHOPIFY_API_POLL_INTERVAL_MS);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      resolve(false);
    }, SHOPIFY_API_POLL_TIMEOUT_MS);
  });
}

const DEBOUNCE_MS = 150;
const SET_TRACKING_CONSENT_TIMEOUT_MS = 3000;

let lastSentSignature: string | null = null;
// `pendingSignature` is set synchronously before dispatching setTrackingConsent
// and cleared from the callback or the 3s timeout. It guards against the
// burst-callback race: if a second consent change arrives while a sync is
// in flight, we dedup it against `pendingSignature` instead of fanning out
// duplicate Shopify calls.
let pendingSignature: string | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Test-only reset for module-level state. Do not call from production code.
 */
export function __resetConsentSyncStateForTests(): void {
  lastSentSignature = null;
  pendingSignature = null;
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}

/** Strip leading dot so SDK receives a bare host. */
function stripLeadingDot(domain: string): string {
  return domain.startsWith('.') ? domain.slice(1) : domain;
}

/** Derive apex domain from NEXT_PUBLIC_COOKIE_DOMAIN (fallback for PR apps). */
function fallbackApexDomain(): string | null {
  const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN;
  if (!cookieDomain || cookieDomain === 'localhost') return null;
  return cookieDomain.startsWith('.') ? cookieDomain.slice(1) : cookieDomain;
}

/** Derive `shop.<apex>` from NEXT_PUBLIC_COOKIE_DOMAIN (checkoutRootDomain fallback). */
function fallbackShopDomain(): string | null {
  const apex = fallbackApexDomain();
  return apex ? `shop.${apex}` : null;
}

/**
 * Resolve `checkoutRootDomain` for `setTrackingConsent`.
 * Must match Shopify store settings or consent cookie won't persist.
 * Reads CHECKOUT_DOMAIN → STOREFRONT_DOMAIN → shop.<apex> fallback.
 */
function getCheckoutRootDomain(): string | null {
  const domain =
    process.env.NEXT_PUBLIC_SHOPIFY_CHECKOUT_DOMAIN ||
    process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_DOMAIN;
  if (domain) return stripLeadingDot(domain);
  return fallbackShopDomain();
}

/**
 * Resolve `storefrontRootDomain` for `setTrackingConsent`.
 * Per Shopify docs: shared top-level domain (e.g. `seed.com`).
 * Reads STOREFRONT_DOMAIN → apex fallback.
 */
function getStorefrontRootDomain(): string | null {
  const domain = process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_DOMAIN;
  if (domain) return stripLeadingDot(domain);
  return fallbackApexDomain();
}

/**
 * Read OneTrust active groups, build a Shopify payload, and call
 * setTrackingConsent if the payload differs from the last successful sync.
 *
 * Idempotent. Debounced 150ms. No-op when:
 *  - process.env.LOCAL === 'true' (local dev never writes .seed.com cookies)
 *  - NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN is missing
 *  - Shopify.customerPrivacy is unavailable (caller should ensure availability)
 */
export function syncConsentToShopify(): void {
  if (typeof window === 'undefined') return;
  if (process.env.LOCAL === 'true') return;

  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;

    const token = process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN;
    if (!token) {
      console.error(
        '[consent-bridge] NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN is not set; skipping consent sync.',
      );
      trackCustomWarning('consent_storefront_token_missing', {});
      return;
    }

    const setTrackingConsent =
      window.Shopify?.customerPrivacy?.setTrackingConsent;
    if (!setTrackingConsent) {
      console.warn(
        '[consent-bridge] Shopify.customerPrivacy.setTrackingConsent unavailable; skipping consent sync.',
      );
      trackCustomWarning('consent_sync_failed', {
        reason: 'shopify_customer_privacy_unavailable',
      });
      return;
    }

    const activeGroups = window.OnetrustActiveGroups || '';
    if (!activeGroups) {
      // Fail-open: if groups are empty inside the flush (e.g. OnConsentChanged
      // fires with cleared groups), do NOT write an all-denied cookie.
      // initializeShopifyConsentBridge gates on isOneTrustReady which requires
      // non-empty groups; this branch flags a regression and bails out so we
      // preserve the region-aware Google Consent Mode defaults rather than
      // overriding them with all-denied.
      console.warn(
        '[consent-bridge] OnetrustActiveGroups is empty inside flush; skipping consent sync (fail-open).',
      );
      trackCustomWarning('consent_groups_empty', {
        reason: 'empty_in_flush',
      });
      return;
    }
    const payload = buildConsentPayload(activeGroups);
    const signature = JSON.stringify(payload);
    // Dedup against both the last-confirmed signature AND any in-flight
    // signature. Without `pendingSignature`, a burst of consent changes that
    // share a payload could fan out into duplicate setTrackingConsent calls
    // before the first callback lands.
    if (signature === lastSentSignature) return;
    if (signature === pendingSignature) return;

    const checkoutDomain = getCheckoutRootDomain();
    const storefrontDomain = getStorefrontRootDomain();
    if (!checkoutDomain || !storefrontDomain) {
      console.error(
        '[consent-bridge] NEXT_PUBLIC_SHOPIFY_CHECKOUT_DOMAIN / NEXT_PUBLIC_SHOPIFY_STOREFRONT_DOMAIN must be set; skipping consent sync.',
      );
      trackCustomWarning('consent_storefront_domain_missing', {});
      return;
    }

    pendingSignature = signature;
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      // Timeout. Do NOT update `lastSentSignature` — leaving it unchanged
      // means a subsequent change with the same payload will pass the dedup
      // check and retry. Clear `pendingSignature` for the same reason.
      if (pendingSignature === signature) pendingSignature = null;
      console.warn(
        '[consent-bridge] setTrackingConsent callback did not fire within 3s; treating as failed.',
      );
      trackCustomWarning('consent_sync_callback_timeout', { signature });
    }, SET_TRACKING_CONSENT_TIMEOUT_MS);

    setTrackingConsent(
      {
        ...payload,
        headlessStorefront: true,
        checkoutRootDomain: checkoutDomain,
        storefrontRootDomain: storefrontDomain,
        storefrontAccessToken: token,
      },
      () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        lastSentSignature = signature;
        if (pendingSignature === signature) pendingSignature = null;
      },
    );
  }, DEBOUNCE_MS);
}

const ONETRUST_POLL_INTERVAL_MS = 100;
const ONETRUST_POLL_TIMEOUT_MS = 10_000;

/**
 * Resolves true once OneTrust is fully ready: both the API surface
 * (`OnConsentChanged`) is callable AND `OnetrustActiveGroups` has been
 * populated with a non-empty string.
 *
 * The non-empty groups requirement is load-bearing for fail-open semantics:
 * OneTrust always includes at least `C0001` once it has computed consent.
 * An empty / undefined value means OneTrust hasn't determined region-aware
 * defaults yet — keep waiting rather than read it as "all denied". If the
 * timeout elapses with groups still empty, the caller should skip the initial
 * sync (no cookie write) rather than write an all-denied cookie.
 */
function isOneTrustReady(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.OneTrust?.OnConsentChanged !== 'function') return false;
  return Boolean(window.OnetrustActiveGroups);
}

function waitForOneTrust(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (isOneTrustReady()) {
    return Promise.resolve(true);
  }

  return new Promise<boolean>((resolve) => {
    const interval = setInterval(() => {
      if (isOneTrustReady()) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve(true);
      }
    }, ONETRUST_POLL_INTERVAL_MS);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      resolve(false);
    }, ONETRUST_POLL_TIMEOUT_MS);
  });
}

let bridgeInitialized = false;

/**
 * Initializes the storefront consent bridge.
 *
 * Steps:
 *  1. Skip when LOCAL=true.
 *  2. Wait for both Shopify.customerPrivacy and OneTrust to be ready
 *     (OneTrust ready = API present AND OnetrustActiveGroups populated).
 *  3. Do an initial sync from current OnetrustActiveGroups.
 *  4. Register an OneTrust.OnConsentChanged listener that re-syncs on every
 *     consent change.
 *
 * Fail-open: if OneTrust never populates groups within the timeout, we skip
 * the initial sync entirely (no cookie write) rather than send an all-denied
 * payload. The site already has region-aware Google Consent Mode defaults via
 * pages/_document.tsx, so writing nothing preserves those defaults.
 *
 * Safe to call multiple times; only the first call performs setup.
 */
export async function initializeShopifyConsentBridge(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (process.env.LOCAL === 'true') return;
  if (bridgeInitialized) return;
  bridgeInitialized = true;

  const [shopifyReady, oneTrustReady] = await Promise.all([
    waitForShopifyCustomerPrivacy(),
    waitForOneTrust(),
  ]);

  if (!shopifyReady) {
    console.warn(
      '[consent-bridge] Shopify Customer Privacy API never loaded; consent will not transit to checkout.',
    );
    trackCustomWarning('consent_sync_failed', {
      reason: 'shopify_customer_privacy_never_loaded',
    });
    return;
  }
  if (!oneTrustReady) {
    console.warn(
      '[consent-bridge] OneTrust never populated active groups; skipping initial sync (fail-open).',
    );
    trackCustomWarning('consent_groups_empty', {
      reason: 'onetrust_groups_never_populated',
    });
    return;
  }

  // OneTrust is fully ready and OnetrustActiveGroups is guaranteed non-empty,
  // so the initial sync will write a meaningful payload (US: all-granted,
  // EU pre-interaction: essential-only).
  syncConsentToShopify();
  window.OneTrust!.OnConsentChanged(() => {
    syncConsentToShopify();
  });
}

/**
 * Test-only reset for the bridge initialized flag.
 */
export function __resetBridgeInitForTests(): void {
  bridgeInitialized = false;
}
